import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Session-scoped confidentiality gate.
 * On first attempt to open any confidential document in this session, the user
 * must tick the confidentiality confirmation before the document opens.
 * Acceptance is remembered per-session (sessionStorage) — refresh re-prompts.
 */
const SESSION_KEY = "ip_confidentiality_accepted";

export function useConfidentialityGate() {
  const [needed, setNeeded] = useState(false);
  const [disclaimer, setDisclaimer] = useState(null);
  const [pendingResolver, setPendingResolver] = useState(null);

  useEffect(() => {
    api.get("/disclaimers/confidential").then((r) => setDisclaimer(r.data)).catch(() => setDisclaimer(null));
  }, []);

  const requestAccess = () =>
    new Promise((resolve) => {
      if (sessionStorage.getItem(SESSION_KEY) === "yes") {
        resolve(true);
        return;
      }
      setPendingResolver(() => resolve);
      setNeeded(true);
    });

  const accept = () => {
    sessionStorage.setItem(SESSION_KEY, "yes");
    setNeeded(false);
    if (pendingResolver) pendingResolver(true);
  };
  const cancel = () => {
    setNeeded(false);
    if (pendingResolver) pendingResolver(false);
  };

  return { needed, disclaimer, requestAccess, accept, cancel };
}

export default function ConfidentialityGateDialog({ needed, disclaimer, onAccept, onCancel }) {
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => { if (!needed) setConfirmed(false); }, [needed]);
  if (!needed || !disclaimer) return null;
  return (
    <Dialog open={needed} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 text-[11px] uppercase tracking-[0.2em] font-bold">
            <ShieldCheck size={14} weight="fill" /> Data Room Access
          </div>
          <DialogTitle className="font-display text-2xl mt-1">
            {disclaimer.title || "Confidential — Professional Clients Only"}
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line" data-testid="confidentiality-body">
          {disclaimer.body}
        </div>
        <label className="flex items-start gap-3 text-sm cursor-pointer mt-3">
          <Checkbox checked={confirmed} onCheckedChange={setConfirmed} data-testid="chk-confidentiality-doc" className="mt-0.5" />
          <span>I confirm I have read and agree to the terms above, and I am an approved Professional Investor.</span>
        </label>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button
            onClick={onAccept}
            disabled={!confirmed}
            data-testid="confidentiality-accept-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            I Acknowledge and Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
